<template>
  <v-app class="dashboard-app">
    <!-- Global Header Component -->
    <sales-agent-header></sales-agent-header>

    <v-main style="padding:0px!important;margin-top: 70px;">
      <!-- Loading Overlay -->
      <v-overlay :value="loading" z-index="999" color="#1a1a2e" opacity="0.9">
        <div class="text-center">
          <v-progress-circular indeterminate size="60" width="4" color="#10b981"></v-progress-circular>
          <p class="mt-4 white--text">Loading dashboard...</p>
        </div>
      </v-overlay>

      <!-- Hero Section with Gradient -->
      <div class="hero-section">
        <div class="hero-pattern"></div>
        <v-container class="hero-container">
          <!-- Desktop Layout -->
          <v-row align="center" class="d-none d-md-flex">
            <v-col cols="12" md="7">
              <div class="d-flex align-center">
                <v-avatar size="80" class="hero-avatar mr-4">
                  <img v-if="avatar" :src="avatar" alt="Profile" />
                  <img v-else src="@/assets/img/avatar.png" alt="Profile" />
                </v-avatar>
                <div>
                  <span class="greeting-text">Welcome back,</span>
                  <h1 class="hero-title">{{ client.first_name }} {{ client.last_name }}</h1>
                  <div class="d-flex align-center mt-2">
                    <v-chip small class="verified-chip mr-2">
                      <v-icon x-small left class="material-icons-round">verified</v-icon>
                      Sales Agent
                    </v-chip>
                    <span class="join-date">Member since {{ formatJoinDate(client.created_at) }}</span>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-right">
              <v-btn class="action-btn-outline mr-2" outlined @click="$router.push('/edit-card')">
                <v-icon small left class="material-icons-round">edit</v-icon>
                Edit Profile
              </v-btn>
              <v-btn class="action-btn-primary" @click="gotoVCard">
                <v-icon small left class="material-icons-round">badge</v-icon>
                View V-Card
              </v-btn>
            </v-col>
          </v-row>

          <!-- Mobile Layout -->
          <div class="d-md-none mobile-hero">
            <v-avatar size="90" class="hero-avatar mb-4">
              <img v-if="avatar" :src="avatar" alt="Profile" />
              <img v-else src="@/assets/img/avatar.png" alt="Profile" />
            </v-avatar>
            <span class="greeting-text">Welcome back,</span>
            <h1 class="hero-title">{{ client.first_name }} {{ client.last_name }}</h1>
            <div class="d-flex align-center justify-center mt-2 mb-4 flex-wrap">
              <v-chip small class="verified-chip mr-2 mb-1">
                <v-icon x-small left class="material-icons-round">verified</v-icon>
                Sales Agent
              </v-chip>
              <span class="join-date mb-1">Member since {{ formatJoinDate(client.created_at) }}</span>
            </div>
            <div class="mobile-hero-actions">
              <v-btn class="action-btn-outline mb-2" outlined block @click="$router.push('/edit-card')">
                <v-icon small left class="material-icons-round">edit</v-icon>
                Edit Profile
              </v-btn>
              <v-btn class="action-btn-primary" block @click="gotoVCard">
                <v-icon small left class="material-icons-round">badge</v-icon>
                View V-Card
              </v-btn>
            </div>
          </div>
        </v-container>
      </div>

      <!-- Dashboard Content -->
      <v-container class="dashboard-content mt-5">

        <!-- Earnings Section -->
        <div class="section-header">EARNINGS OVERVIEW</div>
        <v-row dense class="mb-4">
          <v-col cols="6">
            <div class="white-card">
              <div class="card-top-row">
                <span class="card-label">TOTAL EARNED</span>
                <v-icon small class="material-icons-round" style="color: #3b82f6;">payments</v-icon>
              </div>
              <div class="card-value">${{ totalCommission }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="white-card">
              <div class="card-top-row">
                <span class="card-label">DELIVERED</span>
                <v-icon small class="material-icons-round" style="color: #10b981;">local_shipping</v-icon>
              </div>
              <div class="card-value">{{ deliveredCount }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Status Section with Gradient Cards -->
        <div class="section-header">STATUS</div>
        <v-row dense class="mb-4">
          <v-col cols="6">
            <div class="gradient-card gradient-green">
              <div class="gradient-label">IN PROGRESS</div>
              <div class="gradient-value">{{ applicationStartedCount }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="gradient-card gradient-teal">
              <div class="gradient-label">APPROVED</div>
              <div class="gradient-value">{{ approvedCount }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Referral Statistics -->
        <div class="section-header">REFERRAL STATISTICS</div>
        <v-row dense class="mb-6">
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('all')">
              <div class="mini-label">REFERRALS</div>
              <div class="mini-value">{{ leads_referred.length }}</div>
            </div>
          </v-col>
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('approved')">
              <div class="mini-label">APPROVED</div>
              <div class="mini-value" style="color: #10b981;">{{ approvedCount }}</div>
            </div>
          </v-col>
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('pending')">
              <div class="mini-label">PENDING</div>
              <div class="mini-value" style="color: #f59e0b;">{{ applicationStartedCount }}</div>
            </div>
          </v-col>
          <v-col cols="3">
            <div class="mini-stat-card clickable" @click="goToReferrals('delivered')">
              <div class="mini-label">DELIVERED</div>
              <div class="mini-value" style="color: #3b82f6;">{{ deliveredCount }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Main Grid -->
        <v-row class="mt-2">
          <!-- QR & Share Section -->
          <v-col cols="12" lg="4">
            <v-card class="modern-card qr-section-card">
              <div class="card-header-gradient green-gradient">
                <v-icon class="material-icons-round" size="24">qr_code_2</v-icon>
                <span>Your Referral Code</span>
              </div>
              <v-card-text class="text-center pa-6">
                <div class="qr-container">
                  <div class="qr-frame">
                    <img v-if="qrCodeUrl" :src="qrCodeUrl" class="qr-image" alt="QR Code" />
                    <v-progress-circular v-else indeterminate size="60" color="#10b981"></v-progress-circular>
                  </div>
                </div>

                <div class="referral-link-section mt-5">
                  <label class="link-label">Your Referral Link</label>
                  <div class="link-box">
                    <span class="link-text">{{ referralLink }}</span>
                    <v-btn icon small @click="copyReferralLink" class="copy-btn">
                      <v-icon small class="material-icons-round">content_copy</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div class="action-buttons mt-5">
                  <v-btn block class="share-btn mb-3" @click="shareReferralLink">
                    <v-icon small left class="material-icons-round">share</v-icon>
                    Share Link
                  </v-btn>
                  <v-btn block outlined class="download-btn" @click="downloadQR">
                    <v-icon small left class="material-icons-round">download</v-icon>
                    Download QR
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Recent Referrals -->
          <v-col cols="12" lg="8">
            <v-card class="modern-card referrals-card">
              <div class="card-header-flex clickable" @click="showRecentReferrals = !showRecentReferrals">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20">group</v-icon>
                  </div>
                  <span class="header-title">Recent Referrals</span>
                </div>
                <div class="d-flex align-center">
                  <v-btn text small class="view-all-btn mr-2" v-if="recentReferrals.length > 5" @click.stop="$router.push('/my-referrals')">
                    View All
                    <v-icon small right class="material-icons-round">arrow_forward</v-icon>
                  </v-btn>
                  <v-icon class="material-icons-round" color="grey">{{ showRecentReferrals ? 'expand_less' : 'expand_more' }}</v-icon>
                </div>
              </div>

              <v-expand-transition>
                <div v-show="showRecentReferrals">
                  <v-card-text class="pa-0" v-if="recentReferrals.length > 0">
                    <div class="referrals-list">
                      <div v-for="(referral, index) in recentReferrals.slice(0, 5)" :key="referral.user_id" class="referral-row clickable" :class="{ 'last-row': index === Math.min(recentReferrals.length, 5) - 1 }" @click="viewReferralDetails(referral)">
                        <div class="referral-info">
                          <v-avatar size="44" class="referral-avatar">
                            <img v-if="referral.avatar" :src="wasabi_url + referral.avatar" alt="Avatar" />
                            <span v-else class="avatar-initials">
                              {{ (referral.first_name || 'U').charAt(0) }}{{ (referral.last_name || '').charAt(0) }}
                            </span>
                          </v-avatar>
                          <div class="referral-details">
                            <h4 class="referral-name">{{ referral.first_name }} {{ referral.last_name }}</h4>
                            <span class="referral-date">{{ formatReferralDate(referral.created_at) }}</span>
                          </div>
                        </div>
                        <div class="referral-status-section">
                          <v-chip small class="status-chip" :class="'status-' + (referral.lead_status || 'pending')">
                            {{ formatStatus(referral.lead_status) }}
                          </v-chip>
                          <span class="commission-amount">${{ referral.commission_amount || '50' }}</span>
                        </div>
                      </div>
                    </div>
                  </v-card-text>

                  <!-- Empty State -->
                  <v-card-text v-else class="empty-state-container">
                    <div class="empty-illustration">
                      <v-icon size="80" class="material-icons-round empty-icon">group_add</v-icon>
                    </div>
                    <h3 class="empty-title">No Referrals Yet</h3>
                    <p class="empty-text">Start sharing your referral link to earn commissions!</p>
                    <v-btn class="share-btn" @click.stop="shareReferralLink">
                      <v-icon small left class="material-icons-round">share</v-icon>
                      Share Your Link
                    </v-btn>
                  </v-card-text>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>

        <!-- Referred Locations Map Section -->
        <div class="section-header mt-6">REFERRED LOCATIONS</div>
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card class="locations-map-card" elevation="2">
              <div class="map-container">
                <GmapMap
                  ref="referralMap"
                  :center="mapCenter"
                  :zoom="mapZoom"
                  map-type-id="roadmap"
                  style="width: 100%; height: 380px;"
                  :options="mapOptions"
                  @click="closeInfoWindow"
                >
                  <GmapMarker
                    v-for="(marker, index) in referralMarkers"
                    :key="index"
                    :position="marker.position"
                    :icon="markerIcon"
                    :clickable="true"
                    @click="onMarkerClick(marker, index)"
                  />
                  <GmapInfoWindow
                    :options="infoWindowOptions"
                    :position="infoWindowPosition"
                    :opened="infoWindowOpen"
                    @closeclick="closeInfoWindow"
                  >
                    <div class="info-window-content" v-if="selectedMarker">
                      <div class="info-header">
                        <v-icon small class="material-icons-round mr-2" color="#10b981">person</v-icon>
                        <strong>{{ selectedMarker.name }}</strong>
                      </div>
                      <div class="info-details">
                        <div class="info-row" v-if="selectedMarker.email">
                          <v-icon x-small class="material-icons-round mr-1">email</v-icon>
                          <span>{{ selectedMarker.email }}</span>
                        </div>
                        <div class="info-row" v-if="selectedMarker.location">
                          <v-icon x-small class="material-icons-round mr-1">location_on</v-icon>
                          <span>{{ selectedMarker.location }}</span>
                        </div>
                        <div class="info-row">
                          <v-icon x-small class="material-icons-round mr-1">calendar_today</v-icon>
                          <span>{{ formatDate(selectedMarker.date) }}</span>
                        </div>
                        <div class="info-row" v-if="selectedMarker.referralId">
                          <v-icon x-small class="material-icons-round mr-1">tag</v-icon>
                          <span>ID: {{ selectedMarker.referralId }}</span>
                        </div>
                      </div>
                      <v-btn x-small color="green" dark class="mt-2" @click="zoomToMarker(selectedMarker)">
                        <v-icon x-small left class="material-icons-round">zoom_in</v-icon>
                        Zoom In
                      </v-btn>
                    </div>
                  </GmapInfoWindow>
                </GmapMap>
                <!-- Map Legend -->
                <div class="map-legend">
                  <div class="legend-item">
                    <div class="legend-marker"></div>
                    <span>Referral Location ({{ referralMarkers.length }})</span>
                  </div>
                </div>
                <!-- Reset Zoom Button -->
                <v-btn
                  fab
                  x-small
                  class="reset-zoom-btn"
                  @click="resetMapView"
                  title="Reset Map View"
                >
                  <v-icon small class="material-icons-round">zoom_out_map</v-icon>
                </v-btn>
              </div>
              <div v-if="referralMarkers.length === 0" class="no-locations-overlay">
                <v-icon size="48" class="material-icons-round" color="grey lighten-1">location_off</v-icon>
                <p class="mt-2 grey--text">No referral locations yet</p>
                <p class="text-caption grey--text">Referral locations will appear as pins on the map</p>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Cards Section -->
        <v-row class="mt-4">
          <v-col cols="12">
            <!-- Expand/Collapse All Buttons -->
            <div class="d-flex justify-end mb-3">
              <v-btn text small color="indigo" class="mr-2" @click="expandAllCards">
                <v-icon small left class="material-icons-round">unfold_more</v-icon>
                Expand All
              </v-btn>
              <v-btn text small color="grey darken-1" @click="collapseAllCards">
                <v-icon small left class="material-icons-round">unfold_less</v-icon>
                Hide All
              </v-btn>
            </div>

            <!-- Conversations Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showConversations = !showConversations">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">chat</v-icon>
                  </div>
                  <span class="header-title">Conversations</span>
                </div>
                <v-icon class="material-icons-round">{{ showConversations ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showConversations">
                  <Chat />
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- Reviews Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showReviews = !showReviews">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">star</v-icon>
                  </div>
                  <span class="header-title">Reviews ({{ reviews.length }})</span>
                </div>
                <v-icon class="material-icons-round">{{ showReviews ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showReviews">
                  <div :style="$vuetify.breakpoint.xs ? 'max-height:400px; overflow-y:auto;' : 'max-height:500px; overflow-y:auto;'">
                    <div v-if="reviews.length === 0" class="text-center pa-8">
                      <v-icon size="60" class="material-icons-round" color="grey lighten-1">rate_review</v-icon>
                      <h4 class="mt-3 grey--text">No customer reviews yet</h4>
                    </div>
                    <CardReview v-else :users="reviews" />
                  </div>
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- Leads Referred Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showLeads = !showLeads">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">people_alt</v-icon>
                  </div>
                  <span class="header-title">Leads Referred ({{ leads_referred.length }})</span>
                </div>
                <div class="d-flex align-center">
                  <v-btn v-if="selectedLeads.length > 0" color="red" x-small @click.stop="deleteSelectedLeads" class="mr-2" dark>
                    Delete ({{ selectedLeads.length }})
                  </v-btn>
                  <v-icon class="material-icons-round">{{ showLeads ? 'expand_less' : 'expand_more' }}</v-icon>
                </div>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showLeads" class="pa-0">
                  <div v-if="leads_referred.length === 0" class="text-center pa-8">
                    <v-icon size="60" class="material-icons-round" color="grey lighten-1">person_add</v-icon>
                    <h4 class="mt-3 grey--text">No leads referred yet</h4>
                  </div>
                  <v-data-table
                    v-else
                    :headers="headers_leads"
                    :items="leads_referred"
                    :items-per-page="5"
                    show-select
                    v-model="selectedLeads"
                    item-key="user_id"
                    class="modern-table"
                  >
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="36" class="mr-2">
                          <img v-if="item.avatar" :src="item.avatar" alt="avatar">
                          <v-icon v-else class="material-icons-round" size="30">account_circle</v-icon>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.first_name }} {{ item.last_name }}</div>
                          <div class="text-caption grey--text">{{ item.email }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.lead_status="{ item }">
                      <v-chip small :color="getStatusColor(item.lead_status)" text-color="white">
                        {{ item.lead_status || 'pending' }}
                      </v-chip>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      {{ formatDate(item.created_at) }}
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- Contact Exchange Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showContactExchange = !showContactExchange">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">swap_horiz</v-icon>
                  </div>
                  <span class="header-title">Contact Exchange ({{ contactExchangeLeads.length }})</span>
                </div>
                <v-icon class="material-icons-round">{{ showContactExchange ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showContactExchange" class="pa-0">
                  <div v-if="contactExchangeLeads.length === 0" class="text-center pa-8">
                    <v-icon size="60" class="material-icons-round" color="grey lighten-1">swap_horiz</v-icon>
                    <h4 class="mt-3 grey--text">No contact exchanges yet</h4>
                    <p class="grey--text text-caption">Contacts exchanged via your V-Card will appear here</p>
                  </div>
                  <v-data-table
                    v-else
                    :headers="headers_contact_exchange"
                    :items="contactExchangeLeads"
                    :items-per-page="5"
                    item-key="user_id"
                    class="modern-table"
                  >
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="36" class="mr-2">
                          <v-icon class="material-icons-round" size="30" color="primary">account_circle</v-icon>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.first_name }} {{ item.last_name }}</div>
                          <div class="text-caption grey--text">{{ item.email }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.phone_m="{ item }">
                      <a v-if="item.phone_m" :href="'tel:' + item.phone_m" class="text-decoration-none">
                        {{ item.phone_m }}
                      </a>
                      <span v-else class="grey--text">-</span>
                    </template>
                    <template v-slot:item.memo="{ item }">
                      <span v-if="item.memo" style="white-space: normal; word-break: break-word;">{{ item.memo }}</span>
                      <span v-else class="grey--text">-</span>
                    </template>
                    <template v-slot:item.location="{ item }">
                      <v-btn
                        v-if="item.address_1 || (item.latitude && item.longitude)"
                        x-small
                        color="primary"
                        text
                        @click.stop="openLocationMap(item)"
                      >
                        <v-icon small class="material-icons-round mr-1">location_on</v-icon>
                        View Map
                      </v-btn>
                      <span v-else class="grey--text">-</span>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                      {{ formatDate(item.created_at) }}
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- Sales Representatives Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showSalesReps = !showSalesReps">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">badge</v-icon>
                  </div>
                  <span class="header-title">Sales Representatives ({{ sales_agents.length }})</span>
                </div>
                <v-icon class="material-icons-round">{{ showSalesReps ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showSalesReps" class="pa-0">
                  <div v-if="sales_agents.length === 0" class="text-center pa-8">
                    <v-icon size="60" class="material-icons-round" color="grey lighten-1">groups</v-icon>
                    <h4 class="mt-3 grey--text">No sales representatives yet</h4>
                  </div>
                  <v-data-table
                    v-else
                    :headers="headers_sales"
                    :items="sales_agents"
                    :items-per-page="5"
                    class="modern-table"
                  >
                    <template v-slot:item.first_name="{ item }">
                      {{ item.first_name }} {{ item.last_name }}
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- Referral Representatives Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showRefReps = !showRefReps">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">account_tree</v-icon>
                  </div>
                  <span class="header-title">Referral Representatives ({{ ref_agents_old.length }})</span>
                </div>
                <v-icon class="material-icons-round">{{ showRefReps ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showRefReps">
                  <div :style="$vuetify.breakpoint.xs ? 'width: 100%;overflow-x: auto;' : 'width: 100%;overflow: auto;display: flex;justify-content: center'" class="mt-3">
                    <TreeChart v-if="ref_agents_old.length > 0" :json="ref_agents" />
                    <div v-else class="text-center pa-8">
                      <v-icon size="60" class="material-icons-round" color="grey lighten-1">account_tree</v-icon>
                      <h4 class="mt-3 grey--text">No referral representatives yet</h4>
                    </div>
                  </div>
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- Rental Users Card (if rental_access) -->
            <v-card v-if="client.rental_access" class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showRental = !showRental">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">directions_car</v-icon>
                  </div>
                  <span class="header-title">Rental Users ({{ rental_users.length }})</span>
                </div>
                <v-icon class="material-icons-round">{{ showRental ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showRental" class="pa-0">
                  <div v-if="rental_users.length === 0" class="text-center pa-8">
                    <v-icon size="60" class="material-icons-round" color="grey lighten-1">car_rental</v-icon>
                    <h4 class="mt-3 grey--text">No rental users yet</h4>
                  </div>
                  <v-data-table
                    v-else
                    :headers="headers_sales"
                    :items="rental_users"
                    :items-per-page="5"
                    class="modern-table"
                  >
                    <template v-slot:item.first_name="{ item }">
                      {{ item.first_name }} {{ item.last_name }}
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-expand-transition>
            </v-card>

            <!-- My Rewards Card -->
            <v-card class="modern-card mb-4">
              <div class="card-header-flex clickable" @click="showRewards = !showRewards">
                <div class="d-flex align-center">
                  <div class="header-icon-wrap green-bg">
                    <v-icon class="material-icons-round" size="20" color="white">emoji_events</v-icon>
                  </div>
                  <span class="header-title">My Rewards ({{ userRewards.length }})</span>
                </div>
                <v-icon class="material-icons-round">{{ showRewards ? 'expand_less' : 'expand_more' }}</v-icon>
              </div>
              <v-expand-transition>
                <v-card-text v-show="showRewards">
                  <div v-if="loadingRewards" class="text-center py-4">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </div>
                  <div v-else-if="userRewards.length === 0" class="text-center pa-8">
                    <v-icon size="60" class="material-icons-round" color="grey lighten-1">card_giftcard</v-icon>
                    <h4 class="mt-3 grey--text">No rewards yet</h4>
                  </div>
                  <v-row v-else>
                    <v-col v-for="reward in userRewards" :key="reward.id" cols="12" sm="6" md="4">
                      <v-card class="reward-card" outlined>
                        <v-card-text class="text-center">
                          <v-icon size="40" class="material-icons-round" color="amber">emoji_events</v-icon>
                          <h4 class="mt-2">{{ reward.name }}</h4>
                          <p class="text-caption grey--text">{{ reward.description }}</p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Referral Profile Modal -->
      <v-dialog v-model="referralProfileDialog" max-width="700" scrollable>
        <v-card style="border-radius: 16px; overflow: hidden;">
          <v-card-text v-if="selectedReferral" class="pa-0">
            <!-- Profile Header -->
            <div style="background: linear-gradient(180deg, #2c3e50 0%, #34495e 100%); padding: 32px 24px; position: relative;">
              <!-- Close Button -->
              <v-btn icon dark style="position: absolute; top: 12px; right: 12px; z-index: 1;" @click="referralProfileDialog = false">
                <v-icon class="material-icons-round">close</v-icon>
              </v-btn>

              <div class="d-flex align-start flex-column flex-sm-row" style="gap: 20px;">
                <!-- Avatar -->
                <v-avatar :size="$vuetify.breakpoint.xs ? 80 : 100" style="border: 4px solid rgba(255,255,255,0.2); flex-shrink: 0;" class="mx-auto mx-sm-0">
                  <img v-if="selectedReferral.avatar" :src="wasabi_url + selectedReferral.avatar" alt="avatar">
                  <v-icon v-else class="material-icons-round" :size="$vuetify.breakpoint.xs ? 50 : 60" color="white">account_circle</v-icon>
                </v-avatar>

                <!-- Name and Info -->
                <div style="flex: 1; padding-top: 8px;" class="text-center text-sm-left">
                  <h2 class="white--text mb-2" :style="{ fontSize: $vuetify.breakpoint.xs ? '24px' : '32px', fontWeight: 600, lineHeight: 1.2 }">
                    {{ selectedReferral.first_name }} {{ selectedReferral.last_name }}
                  </h2>
                  <div class="mb-2" style="opacity: 0.9;">
                    <div class="white--text d-flex align-center justify-center justify-sm-start" :style="{ fontSize: $vuetify.breakpoint.xs ? '12px' : '14px' }">
                      <v-icon small class="material-icons-round mr-1" style="color: rgba(255,255,255,0.8);">email</v-icon>
                      {{ selectedReferral.email }}
                    </div>
                    <div v-if="selectedReferral.phone_m" class="white--text d-flex align-center justify-center justify-sm-start mt-1" :style="{ fontSize: $vuetify.breakpoint.xs ? '12px' : '14px' }">
                      <v-icon small class="material-icons-round mr-1" style="color: rgba(255,255,255,0.8);">phone</v-icon>
                      {{ selectedReferral.phone_m }}
                    </div>
                  </div>
                  <div class="d-flex align-center flex-wrap justify-center justify-sm-start" style="gap: 8px;">
                    <v-chip small :color="getStatusColor(selectedReferral.lead_status)" text-color="white" style="font-weight: 600; font-size: 10px; letter-spacing: 0.5px;">
                      {{ formatStatus(selectedReferral.lead_status) }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats Cards Container -->
            <div :style="{ padding: $vuetify.breakpoint.xs ? '24px 16px' : '32px 40px' }">
              <!-- Credit & Income Section -->
              <div class="mb-4">
                <h3 :style="{ color: '#2c3e50', fontSize: $vuetify.breakpoint.xs ? '12px' : '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', opacity: 0.7 }">
                  Credit & Income
                </h3>
                <v-row dense>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: 'rgba(255,255,255,0.9)', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Credit Score</div>
                      <div :style="{ color: '#ffffff', fontSize: $vuetify.breakpoint.xs ? '32px' : '40px', fontWeight: 700, lineHeight: 1 }">{{ selectedReferral.credit || 'N/A' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: 'rgba(255,255,255,0.9)', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Monthly Income</div>
                      <div :style="{ color: '#ffffff', fontSize: $vuetify.breakpoint.xs ? '28px' : '36px', fontWeight: 700, lineHeight: 1 }">${{ selectedReferral.employment_income ? Number(selectedReferral.employment_income).toLocaleString() : '0' }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Met At Section -->
              <div v-if="resolvedAddress || (selectedReferral.latitude && selectedReferral.longitude)" class="mb-4">
                <h3 :style="{ color: '#2c3e50', fontSize: $vuetify.breakpoint.xs ? '12px' : '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', opacity: 0.7 }">
                  Met At
                </h3>
                <div :style="{ background: '#f8fafc', borderLeft: '4px solid #6366F1', padding: $vuetify.breakpoint.xs ? '16px 12px' : '20px 24px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }">
                  <div class="d-flex align-center" style="gap: 12px;">
                    <v-icon class="material-icons-round" color="#6366F1">location_on</v-icon>
                    <div v-if="loadingAddress" :style="{ color: '#64748b', fontSize: $vuetify.breakpoint.xs ? '14px' : '16px', fontStyle: 'italic' }">Loading address...</div>
                    <div v-else :style="{ color: '#1e293b', fontSize: $vuetify.breakpoint.xs ? '14px' : '16px', fontWeight: 500 }">{{ resolvedAddress || 'Location available' }}</div>
                  </div>
                  <v-btn v-if="selectedReferral.latitude && selectedReferral.longitude" x-small color="primary" outlined @click.stop="openLocationMap(selectedReferral)">
                    <v-icon x-small class="material-icons-round mr-1">map</v-icon>
                    View Map
                  </v-btn>
                </div>
              </div>

              <!-- Additional Info -->
              <div class="mb-4">
                <h3 :style="{ color: '#2c3e50', fontSize: $vuetify.breakpoint.xs ? '12px' : '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', opacity: 0.7 }">
                  Referral Details
                </h3>
                <v-row dense>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: '#f8fafc', borderLeft: '4px solid #10b981', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: '#64748b', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Commission</div>
                      <div :style="{ color: '#1e293b', fontSize: $vuetify.breakpoint.xs ? '28px' : '36px', fontWeight: 700, lineHeight: 1 }">${{ selectedReferral.commission_amount || '50' }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="6">
                    <div :style="{ background: '#f8fafc', borderLeft: '4px solid #3b82f6', padding: $vuetify.breakpoint.xs ? '16px 12px' : '24px', borderRadius: '12px', minHeight: $vuetify.breakpoint.xs ? '100px' : '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }">
                      <div :style="{ color: '#64748b', fontSize: $vuetify.breakpoint.xs ? '10px' : '12px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }">Referred On</div>
                      <div :style="{ color: '#1e293b', fontSize: $vuetify.breakpoint.xs ? '16px' : '20px', fontWeight: 700, lineHeight: 1 }">{{ formatDate(selectedReferral.created_at) }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Notes Section -->
              <div class="mb-4">
                <div class="d-flex justify-space-between align-center mb-3">
                  <h3 :style="{ color: '#2c3e50', fontSize: $vuetify.breakpoint.xs ? '12px' : '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.7, margin: 0 }">
                    Notes
                  </h3>
                  <v-btn v-if="!editingNote" text small color="primary" @click="startEditNote" style="text-transform: none;">
                    <v-icon small left class="material-icons-round">{{ selectedReferral.memo ? 'edit' : 'add' }}</v-icon>
                    {{ selectedReferral.memo ? 'Edit' : 'Add Note' }}
                  </v-btn>
                </div>
                <div v-if="editingNote" :style="{ background: '#f8fafc', borderLeft: '4px solid #8b5cf6', padding: $vuetify.breakpoint.xs ? '16px 12px' : '20px 24px', borderRadius: '12px' }">
                  <v-textarea v-model="editNoteText" outlined dense placeholder="Enter note..." rows="3" hide-details class="mb-3"></v-textarea>
                  <div class="d-flex justify-end" style="gap: 8px;">
                    <v-btn small text @click="cancelEditNote">Cancel</v-btn>
                    <v-btn small color="blue" :loading="savingNote" @click="saveNote">Save</v-btn>
                  </div>
                </div>
                <div v-else-if="selectedReferral.memo" :style="{ background: '#f8fafc', borderLeft: '4px solid #8b5cf6', padding: $vuetify.breakpoint.xs ? '16px 12px' : '20px 24px', borderRadius: '12px' }">
                  <div :style="{ color: '#1e293b', fontSize: $vuetify.breakpoint.xs ? '14px' : '15px', lineHeight: 1.6 }">{{ selectedReferral.memo }}</div>
                  <div :style="{ color: '#94a3b8', fontSize: '12px', marginTop: '12px' }">{{ formatDate(selectedReferral.updated_at || selectedReferral.created_at) }}</div>
                </div>
                <div v-else :style="{ background: '#f8fafc', borderLeft: '4px solid #8b5cf6', padding: $vuetify.breakpoint.xs ? '16px 12px' : '20px 24px', borderRadius: '12px', color: '#94a3b8', fontStyle: 'italic' }">
                  No notes added yet
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex flex-wrap" style="gap: 12px;">
                <v-btn v-if="selectedReferral.phone_m" :href="'tel:' + selectedReferral.phone_m" color="#10b981" dark class="font-weight-600" :style="{ borderRadius: '10px', textTransform: 'none', flex: 1 }">
                  <v-icon left class="material-icons-round">phone</v-icon>
                  Call
                </v-btn>
                <v-btn v-if="selectedReferral.email" :href="'mailto:' + selectedReferral.email" color="#3b82f6" dark class="font-weight-600" :style="{ borderRadius: '10px', textTransform: 'none', flex: 1 }">
                  <v-icon left class="material-icons-round">email</v-icon>
                  Email
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
import Vue from 'vue';
import TreeChart from "vue-tree-chart";
import CardReview from "@/components/Forms/ReviewCard.vue";
import Chat from "@/components/Forms/Chat.vue";

export default {
  name: 'SalesAgentDashboard',
  components: {
    TreeChart,
    CardReview,
    Chat
  },
  data() {
    return {
      loading: true,
      client: {},
      avatar: '',
      vcard: [],
      leads_referred: [],
      recentReferrals: [],
      selectedLeads: [],
      sales_agents: [],
      rental_users: [],
      ref_agents: [],
      ref_agents_old: [],
      reviews: [],
      userRewards: [],
      loadingRewards: false,
      approvedCount: 0,
      applicationStartedCount: 0,
      deliveredCount: 0,
      totalCommission: 0,
      referralLink: '',
      qrCodeUrl: '',
      wasabi_url: process.env.VUE_APP_WASBI_URL,
      base_url_img: process.env.VUE_APP_WASBI_URL,
      user_id: localStorage.getItem('user_id'),
      // Card visibility states
      showRecentReferrals: true,
      showConversations: true,
      showReviews: true,
      showLeads: true,
      showContactExchange: true,
      showSalesReps: true,
      showRefReps: true,
      showRental: true,
      showRewards: true,
      // Table headers
      headers_leads: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Date Added', value: 'created_at', align: 'center' },
        { text: 'Status', value: 'lead_status', align: 'center' },
        { text: 'Email', value: 'email', align: 'center' }
      ],
      headers_contact_exchange: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Phone', value: 'phone_m', align: 'center' },
        { text: 'Note', value: 'memo', align: 'center' },
        { text: 'Location', value: 'location', align: 'center', sortable: false },
        { text: 'Date', value: 'created_at', align: 'center' }
      ],
      headers_sales: [
        { text: 'Name', value: 'first_name' },
        { text: 'Email', value: 'email' },
        { text: 'Phone', value: 'phone_m' }
      ],
      header: {
        headers: {
          Authorization: "Bearer " + localStorage.getItem('token')
        }
      },
      // Referral profile modal
      referralProfileDialog: false,
      selectedReferral: null,
      resolvedAddress: '',
      loadingAddress: false,
      // Notes editing
      editingNote: false,
      editNoteText: '',
      savingNote: false,
      // Map data
      mapCenter: { lat: 39.8283, lng: -98.5795 }, // Center of US
      mapZoom: 4,
      defaultMapCenter: { lat: 39.8283, lng: -98.5795 },
      defaultMapZoom: 4,
      referralMarkers: [],
      selectedMarker: null,
      infoWindowOpen: false,
      infoWindowPosition: null,
      markerIcon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path fill="#ef4444" d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26c0-8.837-7.163-16-16-16zm0 24c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z"/><circle fill="#fff" cx="16" cy="16" r="5"/></svg>'),
        scaledSize: { width: 32, height: 42 },
        anchor: { x: 16, y: 42 }
      },
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        minZoom: 3,
        restriction: {
          latLngBounds: {
            north: 50,
            south: 24,
            west: -125,
            east: -66
          },
          strictBounds: false
        },
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ]
      },
      infoWindowOptions: {
        pixelOffset: { width: 0, height: -42 }
      }
    }
  },
  computed: {
    contactExchangeLeads() {
      return this.leads_referred.filter(lead => lead.lead_source === 'card_exchange');
    }
  },
  mounted() {
    if (!localStorage.getItem('token')) {
      this.$router.push('/login');
      return;
    }
    this.getUserProfile();
    this.getUserRewards();
  },
  methods: {
    async getUserProfile() {
      this.loading = true;
      try {
        const response = await axios.get("/api/get-client/" + this.user_id, this.header);
        const data = response.data.data;
        this.client = data;
        this.avatar = data.avatar ? this.wasabi_url + data.avatar : '';
        this.vcard = response.data.vcard;
        this.leads_referred = response.data.leads_referred || [];
        this.recentReferrals = this.leads_referred;
        this.sales_agents = response.data.sales_agents || [];
        this.rental_users = response.data.rental_users || [];
        this.ref_agents = response.data.ref_agents_tree || [];
        this.ref_agents_old = response.data.ref_agents || [];
        this.reviews = response.data.reviews || [];
        this.calculatePipelineStats();
        this.generateReferralLink();
        this.buildReferralMarkers();
        await this.checkAndLoadQRCode();
      } catch (error) {
        console.error('Error fetching user profile:', error);
        Vue.$toast.error('Failed to load profile data', { position: 'top-right' });
      } finally {
        this.loading = false;
      }
    },
    async getUserRewards() {
      this.loadingRewards = true;
      try {
        const response = await axios.get("/api/user-rewards/" + this.user_id, this.header);
        if (response.data.flag === 1) {
          this.userRewards = response.data.data || [];
        }
      } catch (error) {
        console.error('Error fetching rewards:', error);
      } finally {
        this.loadingRewards = false;
      }
    },
    async checkAndLoadQRCode() {
      const qrUrl = this.base_url_img + 'vcard/' + this.user_id + '-QR.png';
      try {
        const response = await fetch(qrUrl, { method: 'HEAD' });
        if (response.ok) {
          this.qrCodeUrl = qrUrl;
        } else {
          await this.generateQRCode();
        }
      } catch (error) {
        await this.generateQRCode();
      }
    },
    async generateQRCode() {
      try {
        const response = await axios.post('/api/generate-user-qr', { user_id: this.user_id });
        if (response.data.flag === 1) {
          this.qrCodeUrl = response.data.qr_url;
        }
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    },
    calculatePipelineStats() {
      this.approvedCount = this.leads_referred.filter(l => l.lead_status === 'approved').length;
      this.applicationStartedCount = this.leads_referred.filter(l =>
        l.lead_status === 'pending' || l.lead_status === 'in_progress' || !l.lead_status
      ).length;
      this.deliveredCount = this.leads_referred.filter(l => l.lead_status === 'delivered').length;
      this.totalCommission = this.deliveredCount * 50;
    },
    generateReferralLink() {
      if (this.client.domain) {
        this.referralLink = `https://${this.client.domain}.autosyncx.com`;
      } else {
        this.referralLink = `${window.location.origin}/form-ref/${this.user_id}`;
      }
    },
    gotoVCard() {
      window.open(this.referralLink, '_blank');
    },
    copyReferralLink() {
      navigator.clipboard.writeText(this.referralLink);
      Vue.$toast.success('Link copied!', { position: 'top-right' });
    },
    shareReferralLink() {
      if (navigator.share) {
        navigator.share({ title: 'My Referral Link', url: this.referralLink });
      } else {
        this.copyReferralLink();
      }
    },
    downloadQR() {
      if (this.qrCodeUrl) {
        const link = document.createElement('a');
        link.href = this.qrCodeUrl;
        link.download = 'referral-qr.png';
        link.click();
      }
    },
    formatJoinDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    },
    formatReferralDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    openLocationMap(contact) {
      let mapUrl = '';
      if (contact.latitude && contact.longitude) {
        mapUrl = `https://www.google.com/maps?q=${contact.latitude},${contact.longitude}`;
      } else if (contact.address_1) {
        mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address_1)}`;
      }
      if (mapUrl) {
        window.open(mapUrl, '_blank');
      }
    },
    formatStatus(status) {
      if (!status) return 'Pending';
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    },
    getStatusColor(status) {
      const colors = {
        'approved': 'success',
        'pending': 'warning',
        'in_progress': 'info',
        'delivered': 'primary',
        'rejected': 'error'
      };
      return colors[status] || 'grey';
    },
    goToReferrals(filter) {
      this.$router.push({ path: '/my-referrals', query: { status: filter } });
    },
    async viewReferralDetails(referral) {
      this.selectedReferral = referral;
      this.resolvedAddress = '';
      this.loadingAddress = false;
      this.editingNote = false;
      this.editNoteText = '';
      this.referralProfileDialog = true;
      await this.resolveAddress(referral);
    },
    startEditNote() {
      this.editNoteText = this.selectedReferral.memo || '';
      this.editingNote = true;
    },
    cancelEditNote() {
      this.editingNote = false;
      this.editNoteText = '';
    },
    async saveNote() {
      this.savingNote = true;
      try {
        await axios.post('/api/save-client-memo', {
          user_id: this.selectedReferral.user_id,
          memo: this.editNoteText
        }, this.header);
        this.selectedReferral.memo = this.editNoteText;
        this.selectedReferral.updated_at = new Date().toISOString();
        this.editingNote = false;
        // Update in local array too
        const item = this.recentReferrals.find(r => r.user_id === this.selectedReferral.user_id);
        if (item) item.memo = this.editNoteText;
        this.$toast.success('Note saved successfully');
      } catch (error) {
        console.error('Failed to save note:', error);
        this.$toast.error('Failed to save note');
      } finally {
        this.savingNote = false;
      }
    },
    async resolveAddress(referral) {
      if (!referral) {
        this.resolvedAddress = '';
        return;
      }

      // Helper function to check if value is a real address (contains letters, not just coordinates)
      const isRealAddress = (val) => val && /[a-zA-Z]/.test(val);

      // Build address from available fields (only if they contain actual text, not coordinates)
      let parts = [];
      if (isRealAddress(referral.city)) parts.push(referral.city);
      if (isRealAddress(referral.state)) parts.push(referral.state);
      if (referral.zip && /^[0-9-]+$/.test(referral.zip)) parts.push(referral.zip);

      // If we have valid city/state/zip, use that
      if (parts.length > 0) {
        this.resolvedAddress = parts.join(', ');
        return;
      }

      // If address_1 looks like a real address (contains letters, not just numbers/coordinates)
      if (isRealAddress(referral.address_1)) {
        this.resolvedAddress = referral.address_1;
        return;
      }

      // If we have coordinates, reverse geocode to get address
      if (referral.latitude && referral.longitude) {
        this.loadingAddress = true;
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${referral.latitude},${referral.longitude}&key=${process.env.VUE_APP_GOOGLE_API_KEY}`
          );
          const data = await response.json();
          if (data.status === 'OK' && data.results && data.results.length > 0) {
            // Get formatted address from first result
            this.resolvedAddress = data.results[0].formatted_address;
          } else {
            this.resolvedAddress = 'Location available';
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          this.resolvedAddress = 'Location available';
        } finally {
          this.loadingAddress = false;
        }
        return;
      }

      this.resolvedAddress = '';
    },
    async deleteSelectedLeads() {
      if (!confirm('Are you sure you want to delete selected leads?')) return;
      try {
        const ids = this.selectedLeads.map(l => l.user_id);
        await axios.post('/api/delete-leads', { ids }, this.header);
        this.leads_referred = this.leads_referred.filter(l => !ids.includes(l.user_id));
        this.selectedLeads = [];
        Vue.$toast.success('Leads deleted successfully', { position: 'top-right' });
      } catch (error) {
        Vue.$toast.error('Failed to delete leads', { position: 'top-right' });
      }
    },
    // Map methods
    buildReferralMarkers() {
      this.referralMarkers = [];
      this.leads_referred.forEach(lead => {
        if (lead.latitude && lead.longitude) {
          this.referralMarkers.push({
            position: {
              lat: parseFloat(lead.latitude),
              lng: parseFloat(lead.longitude)
            },
            name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
            email: lead.email || '',
            location: this.formatLeadLocation(lead),
            date: lead.created_at,
            referralId: lead.user_id ? lead.user_id.substring(0, 8).toUpperCase() : null,
            lead: lead
          });
        }
      });
      // Fit map to show all markers if there are any
      if (this.referralMarkers.length > 0) {
        this.$nextTick(() => {
          this.fitMapToBounds();
        });
      }
    },
    formatLeadLocation(lead) {
      const parts = [];
      if (lead.city) parts.push(lead.city);
      if (lead.state) parts.push(lead.state);
      if (lead.zip) parts.push(lead.zip);
      return parts.length > 0 ? parts.join(', ') : (lead.address_1 || '');
    },
    fitMapToBounds() {
      if (this.referralMarkers.length === 0) return;

      if (this.referralMarkers.length === 1) {
        this.mapCenter = this.referralMarkers[0].position;
        this.mapZoom = 10;
        return;
      }

      // Calculate bounds
      let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
      this.referralMarkers.forEach(marker => {
        minLat = Math.min(minLat, marker.position.lat);
        maxLat = Math.max(maxLat, marker.position.lat);
        minLng = Math.min(minLng, marker.position.lng);
        maxLng = Math.max(maxLng, marker.position.lng);
      });

      // Set center
      this.mapCenter = {
        lat: (minLat + maxLat) / 2,
        lng: (minLng + maxLng) / 2
      };

      // Calculate appropriate zoom level
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      if (maxDiff > 50) this.mapZoom = 3;
      else if (maxDiff > 20) this.mapZoom = 4;
      else if (maxDiff > 10) this.mapZoom = 5;
      else if (maxDiff > 5) this.mapZoom = 6;
      else if (maxDiff > 2) this.mapZoom = 7;
      else if (maxDiff > 1) this.mapZoom = 8;
      else this.mapZoom = 9;
    },
    onMarkerClick(marker, index) {
      this.selectedMarker = marker;
      this.infoWindowPosition = marker.position;
      this.infoWindowOpen = true;
    },
    closeInfoWindow() {
      this.infoWindowOpen = false;
      this.selectedMarker = null;
    },
    zoomToMarker(marker) {
      this.mapCenter = marker.position;
      this.mapZoom = 12;
      this.closeInfoWindow();
    },
    resetMapView() {
      if (this.referralMarkers.length > 0) {
        this.fitMapToBounds();
      } else {
        this.mapCenter = this.defaultMapCenter;
        this.mapZoom = this.defaultMapZoom;
      }
      this.closeInfoWindow();
    },
    expandAllCards() {
      this.showRecentReferrals = true;
      this.showConversations = true;
      this.showReviews = true;
      this.showLeads = true;
      this.showContactExchange = true;
      this.showSalesReps = true;
      this.showRefReps = true;
      this.showRental = true;
      this.showRewards = true;
    },
    collapseAllCards() {
      this.showRecentReferrals = false;
      this.showConversations = false;
      this.showReviews = false;
      this.showLeads = false;
      this.showContactExchange = false;
      this.showSalesReps = false;
      this.showRefReps = false;
      this.showRental = false;
      this.showRewards = false;
    }
  }
};
</script>

<style scoped>
/* Dashboard App */
.dashboard-app {
  background: #f8fafc !important;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
  padding: 40px 0;
  position: relative;
  overflow: hidden;
}

.hero-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                    radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 40%);
}

.hero-container {
  position: relative;
  z-index: 1;
}

.hero-avatar {
  border: 4px solid rgba(255,255,255,0.3);
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}

.greeting-text {
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  font-weight: 500;
  display: block;
}

.hero-title {
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
}

.verified-chip {
  background: rgba(255,255,255,0.2) !important;
  color: white !important;
  font-weight: 600;
}

.join-date {
  color: rgba(255,255,255,0.7);
  font-size: 13px;
}

/* Mobile Hero */
.mobile-hero {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mobile-hero .hero-title {
  font-size: 22px;
}

.mobile-hero-actions {
  width: 100%;
  max-width: 280px;
}

/* Action Buttons */
.action-btn-outline {
  border-color: rgba(255,255,255,0.5) !important;
  color: white !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 10px;
}

.action-btn-primary {
  background: white !important;
  color: #10b981 !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 10px;
}

/* Dashboard Content */
.dashboard-content {
  max-width: 1400px;
}

/* Section Headers */
.section-header {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 1px;
  margin-bottom: 12px;
}

/* White Cards */
.white-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.card-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.card-value {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
}

/* Gradient Cards */
.gradient-card {
  border-radius: 12px;
  padding: 20px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.gradient-green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.gradient-teal {
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
}

.gradient-label {
  color: rgba(255,255,255,0.9);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.gradient-value {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
}

/* Mini Stat Cards */
.mini-stat-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 8px;
  text-align: center;
  transition: all 0.2s ease;
}

.mini-stat-card.clickable {
  cursor: pointer;
}

.mini-stat-card.clickable:hover {
  background: #f8fafc;
  border-color: #10b981;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.mini-label {
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
}

.mini-value {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

/* Modern Cards */
.modern-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05) !important;
  overflow: hidden;
}

.qr-section-card {
  background: white;
}

.card-header-gradient {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 600;
}

.card-header-gradient.green-gradient {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.qr-container {
  display: flex;
  justify-content: center;
}

.qr-frame {
  background: white;
  padding: 16px;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  display: inline-block;
}

.qr-image {
  width: 160px;
  height: 160px;
}

.referral-link-section {
  text-align: left;
}

.link-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  display: block;
  margin-bottom: 8px;
}

.link-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.link-text {
  font-size: 13px;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.copy-btn {
  color: #10b981 !important;
}

.share-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 10px;
}

.download-btn {
  border-color: #10b981 !important;
  color: #10b981 !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 10px;
}

/* Referrals Card */
.referrals-card {
  height: 100%;
}

.card-header-flex {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f1f5f9;
}

.header-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: white;
}

.header-icon-wrap.green-bg {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.view-all-btn {
  color: #10b981 !important;
  text-transform: none;
  font-weight: 600;
}

/* Referrals List */
.referrals-list {
  padding: 0;
}

.referral-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.referral-row.clickable {
  cursor: pointer;
}

.referral-row:hover {
  background: #f0fdf4;
}

.referral-row.last-row {
  border-bottom: none;
}

.referral-info {
  display: flex;
  align-items: center;
}

.referral-avatar {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  margin-right: 12px;
}

.avatar-initials {
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.referral-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.referral-date {
  font-size: 12px;
  color: #94a3b8;
}

.referral-status-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-chip {
  font-weight: 600;
  font-size: 11px !important;
}

.status-pending { background: #fef3c7 !important; color: #92400e !important; }
.status-approved { background: #d1fae5 !important; color: #065f46 !important; }
.status-delivered { background: #dbeafe !important; color: #1e40af !important; }
.status-in_progress { background: #e0e7ff !important; color: #3730a3 !important; }

.commission-amount {
  font-size: 14px;
  font-weight: 700;
  color: #10b981;
}

/* Empty State */
.empty-state-container {
  text-align: center;
  padding: 48px 24px;
}

.empty-icon {
  color: #e2e8f0 !important;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #64748b;
  margin: 16px 0 8px;
}

.empty-text {
  font-size: 14px;
  color: #94a3b8;
  margin-bottom: 24px;
}

/* Clickable Card Header */
.card-header-flex.clickable {
  cursor: pointer;
  user-select: none;
}

.card-header-flex.clickable:hover {
  background: #f8fafc;
}

.header-icon-wrap.green-bg {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

/* Modern Table */
.modern-table {
  background: transparent !important;
}

/* Reward Card */
.reward-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease;
}

.reward-card:hover {
  transform: translateY(-2px);
}

/* Mobile Responsive */
@media (max-width: 600px) {
  .hero-section {
    padding: 24px 0;
  }

  .card-value {
    font-size: 24px;
  }

  .gradient-value {
    font-size: 24px;
  }

  .mini-value {
    font-size: 18px;
  }

  .mini-label {
    font-size: 8px;
  }

  .qr-image {
    width: 120px;
    height: 120px;
  }
}

/* Referred Locations Map Styles */
.locations-map-card {
  border-radius: 16px !important;
  overflow: hidden;
  position: relative;
}

.map-container {
  position: relative;
  width: 100%;
}

.map-legend {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.95);
  padding: 10px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
}

.legend-marker {
  width: 14px;
  height: 14px;
  background-color: #ef4444;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.reset-zoom-btn {
  position: absolute !important;
  top: 20px;
  right: 20px;
  background: white !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
  z-index: 1;
}

.reset-zoom-btn .v-icon {
  color: #374151 !important;
}

.no-locations-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  z-index: 2;
}

/* Info Window Styles */
.info-window-content {
  padding: 8px;
  min-width: 200px;
  max-width: 280px;
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 15px;
  color: #111827;
}

.info-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6b7280;
}

.info-row .v-icon {
  color: #9ca3af !important;
  margin-right: 6px;
}

@media (max-width: 600px) {
  .map-legend {
    bottom: 10px;
    left: 10px;
    padding: 8px 12px;
  }

  .legend-item {
    font-size: 11px;
  }

  .reset-zoom-btn {
    top: 10px;
    right: 10px;
  }

  .info-window-content {
    min-width: 180px;
    max-width: 220px;
  }
}
</style>

